import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Folder, Download, Eye, Upload, Trash, Plus, X, FilePdf, FileText, Camera } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { fmtDateFR } from '../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FOLDERS = [
  { key: 'fiche_paie', label_fr: 'Fiches de paie', label_en: 'Payslips', icon: FilePdf, color: '#1565C0' },
  { key: 'doc_admin', label_fr: 'Documents administratifs', label_en: 'Administrative Docs', icon: FileText, color: '#2E7D32' },
  { key: 'autre', label_fr: 'Autres documents', label_en: 'Other Documents', icon: Folder, color: '#E65100' },
];

const MyDocumentsPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const isAdmin = user?.role === 'admin';
  const [docs, setDocs] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');

  const targetUserId = isAdmin ? selectedEmployee : user?.id;

  const fetchDocs = useCallback(async () => {
    if (!targetUserId) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API_URL}/api/users/${targetUserId}/personal-docs`, { withCredentials: true });
      setDocs(res.data);
    } catch (e) { toast.error('Erreur chargement documents'); }
    finally { setLoading(false); }
  }, [targetUserId, API_URL]);

  useEffect(() => {
    const fetchEmployees = async () => {
      if (isAdmin) {
        try {
          const res = await axios.get(`${API_URL}/api/users/employees`, { withCredentials: true });
          setEmployees(res.data);
          if (res.data.length > 0 && !selectedEmployee) setSelectedEmployee(res.data[0].id);
        } catch (e) { console.error('Erreur chargement employes:', e); }
      }
    };
    fetchEmployees();
  }, [isAdmin, selectedEmployee, API_URL]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const effectiveUserId = isAdmin ? selectedEmployee : (user?.id || '');

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !activeFolder || !effectiveUserId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('doc_category', activeFolder);
      formData.append('title', uploadTitle || uploadFile.name);
      await axios.post(`${API_URL}/api/users/${effectiveUserId}/personal-docs`, formData, { withCredentials: true });
      toast.success('Document ajoute');
      setShowUpload(false);
      setUploadFile(null);
      setUploadTitle('');
      fetchDocs();
    } catch (e) { toast.error('Erreur upload'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`${API_URL}/api/users/${effectiveUserId}/personal-docs/${docId}`, { withCredentials: true });
      toast.success('Document supprime');
      fetchDocs();
    } catch (e) { toast.error('Erreur suppression'); }
  };

  const folderDocs = docs.filter(d => d.doc_category === activeFolder);

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="my-documents-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">{isAdmin ? 'Documents des salaries' : 'Mes documents'}</h1>
          <p className="page-subtitle">{isAdmin ? 'Gerez les documents de chaque salarie' : 'Consultez et telechargez vos documents'}</p>
        </div>
      </div>

      {isAdmin && (
        <div style={{ marginBottom: '24px' }}>
          <label className="form-label">Selectionner un salarie</label>
          <select className="form-input" value={selectedEmployee} onChange={(e) => { setSelectedEmployee(e.target.value); setActiveFolder(null); }} data-testid="employee-selector" style={{ maxWidth: '300px' }}>
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>{emp.name}</option>
            ))}
          </select>
        </div>
      )}

      {!activeFolder ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
          {FOLDERS.map(folder => {
            const count = docs.filter(d => d.doc_category === folder.key).length;
            return (
              <div
                key={folder.key}
                data-testid={`folder-${folder.key}`}
                onClick={() => setActiveFolder(folder.key)}
                className="card"
                style={{ cursor: 'pointer', padding: '24px', textAlign: 'center', transition: 'transform 0.15s, box-shadow 0.15s', border: `2px solid ${folder.color}20` }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
              >
                <folder.icon size={48} weight="duotone" style={{ color: folder.color, marginBottom: '12px' }} />
                <h3 style={{ fontWeight: '700', fontSize: '1rem', margin: '0 0 8px', color: 'var(--text-primary, #0A0B10)' }}>
                  {language === 'en' ? folder.label_en : folder.label_fr}
                </h3>
                <span style={{ fontSize: '0.8rem', color: '#5E5F66' }}>{count} document(s)</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <button className="btn btn-secondary" onClick={() => setActiveFolder(null)} data-testid="back-to-folders">
              ← Retour
            </button>
            <h3 style={{ margin: 0, fontWeight: '600' }}>
              {FOLDERS.find(f => f.key === activeFolder)?.[language === 'en' ? 'label_en' : 'label_fr']}
            </h3>
            {(isAdmin || activeFolder === 'autre') && (
              <button className="btn btn-primary" onClick={() => setShowUpload(true)} data-testid="upload-doc-btn">
                <Plus size={16} /> Ajouter
              </button>
            )}
          </div>

          {folderDocs.length === 0 ? (
            <div className="empty-state" style={{ padding: '48px', textAlign: 'center' }}>
              <Folder size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p style={{ color: '#5E5F66' }}>Aucun document dans ce dossier</p>
            </div>
          ) : (
            <div className="card">
              <table className="data-table" data-testid="docs-table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Date d'ajout</th>
                    <th>Ajoute par</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {folderDocs.map(doc => (
                    <tr key={doc.id}>
                      <td style={{ fontWeight: '600' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FileText size={18} style={{ color: '#1565C0' }} />
                          {doc.title || doc.original_filename}
                        </div>
                      </td>
                      <td style={{ fontSize: '0.8rem' }}>{fmtDateFR(doc.uploaded_at)}</td>
                      <td style={{ fontSize: '0.8rem' }}>{doc.uploaded_by}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button className="btn btn-sm btn-secondary" onClick={() => window.open(`${API_URL}/api/users/personal-docs/file/${doc.filename}`, '_blank')} data-testid={`view-doc-${doc.id}`}>
                            <Eye size={14} /> Voir
                          </button>
                          <a className="btn btn-sm btn-secondary" href={`${API_URL}/api/users/personal-docs/file/${doc.filename}`} download data-testid={`download-doc-${doc.id}`}>
                            <Download size={14} />
                          </a>
                          {isAdmin && (
                            <button className="btn btn-sm btn-secondary" style={{ color: '#C62828' }} onClick={() => handleDelete(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                              <Trash size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showUpload && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>Ajouter un document</h3>
              <button onClick={() => { setShowUpload(false); setUploadFile(null); setUploadTitle(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
            </div>
            <form onSubmit={handleUpload} style={{ padding: '24px' }}>
              <div className="form-group">
                <label className="form-label">Titre</label>
                <input className="form-input" value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} placeholder="Nom du document" data-testid="doc-title-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Fichier *</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <label className="btn btn-secondary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer' }}>
                    <Upload size={16} /> Fichier
                    <input type="file" onChange={(e) => setUploadFile(e.target.files[0])} style={{ display: 'none' }} data-testid="doc-file-input" />
                  </label>
                  <label className="btn btn-primary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer' }}>
                    <Camera size={16} /> Photo
                    <input type="file" accept="image/*" capture="environment" onChange={(e) => setUploadFile(e.target.files[0])} style={{ display: 'none' }} data-testid="doc-camera-input" />
                  </label>
                </div>
                {uploadFile && <div style={{ fontSize: '0.8rem', color: '#2E7D32', fontWeight: '500', marginTop: '8px' }}>{uploadFile.name}</div>}
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowUpload(false); setUploadFile(null); }}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={!uploadFile || uploading} data-testid="submit-upload-btn">
                  {uploading ? 'Upload...' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyDocumentsPage;
